import { useEffect, useMemo, useState } from "react"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { agrogestao } from "@/api/agrogestaoClient"
import { useQuery } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"

export default function LCDPR() {
  const propriedadeId = typeof localStorage !== 'undefined' ? localStorage.getItem('propriedade_selecionada') : ''
  const [year, setYear] = useState(String(new Date().getFullYear()))
  const [onlyPaid, setOnlyPaid] = useState(true)
  const [onlyCash, setOnlyCash] = useState(false)
  const [user, setUser] = useState(null)
  const [monthFilter, setMonthFilter] = useState('todos')

  useEffect(() => {
    const run = async () => { const u = await agrogestao.auth.me(); setUser(u) }
    run()
  }, [])

  const { data: propriedade } = useQuery({
    queryKey: ['lcdpr_prop', propriedadeId],
    queryFn: () => propriedadeId ? agrogestao.entities.Propriedade.filter({ id: propriedadeId }).then(r => r[0]) : Promise.resolve(null),
    enabled: !!propriedadeId
  })

  const { data: userInfo } = useQuery({
    queryKey: ['lcdpr_user', user?.email],
    queryFn: async () => {
      if (!user?.email) return null
      const { data } = await agrogestao.client.from('users').select('*').eq('email', user.email).maybeSingle()
      return data || null
    },
    enabled: !!user?.email
  })

  const startDate = `${year}-01-01`
  const endDate = `${year}-12-31`

  const { data: lancamentos = [], isLoading } = useQuery({
    queryKey: ['lcdpr_lanc', propriedadeId, year, onlyPaid, onlyCash],
    queryFn: async () => {
      const { data, error } = await agrogestao.client
        .from('vw_lancamentos_contabilidade')
        .select('*')
        .eq('propriedade_id', propriedadeId)
        .gte('data', startDate)
        .lte('data', endDate)
        .order('data', { ascending: true })
      if (error) return []
      let rows = Array.isArray(data) ? data : []
      if (onlyPaid) rows = rows.filter(r => String(r.status || 'pago') !== 'pendente')
      if (onlyCash) rows = rows.filter(r => String(r.forma_pagamento || '').toLowerCase() === 'dinheiro')
      return rows
    },
    enabled: !!propriedadeId
  })

  const normalized = useMemo(() => {
    const mapCat = (tipo, cat) => {
      const c = String(cat || '').toLowerCase()
      if (tipo === 'receita') {
        if (c.includes('grão') || c.includes('grao') || c.includes('soja') || c.includes('milho')) return 'Venda de Produção Agrícola'
        if (c.includes('animal') || c.includes('gado')) return 'Venda de Produção Pecuária'
        if (c.includes('leite')) return 'Venda de Leite'
        if (c.includes('serv') || c.includes('prest')) return 'Prestação de Serviços'
        if (c.includes('arrend')) return 'Arrendamento Recebido'
        return 'Outras Receitas'
      } else {
        if (c.includes('insumo') || c.includes('defensivo') || c.includes('fertilizante') || c.includes('semente')) return 'Insumos Agrícolas'
        if (c.includes('combust')) return 'Combustível'
        if (c.includes('manuten')) return 'Manutenção de Máquinas'
        if (c.includes('mão de obra') || c.includes('mao de obra') || c.includes('salár') || c.includes('salario')) return 'Mão de Obra'
        if (c.includes('energia')) return 'Energia Elétrica'
        if (c.includes('arrend')) return 'Arrendamento Pago'
        if (c.includes('frete') || c.includes('transporte')) return 'Transporte/Frete'
        if (c.includes('imposto') || c.includes('taxa')) return 'Impostos e Taxas'
        if (c.includes('vet') || c.includes('ração') || c.includes('racao')) return 'Custos Pecuária'
        if (c.includes('financiamento')) return 'Financiamento (Parcela)'
        return 'Outras Despesas'
      }
    }
    return (lancamentos || []).map(r => ({
      data: r.data,
      tipo: r.tipo,
      categoria: mapCat(r.tipo, r.categoria),
      descricao: r.descricao || '',
      pessoa: r.fornecedor_cliente || '',
      forma_pagamento: r.forma_pagamento || '',
      valor: Number(r.valor || 0),
      safra_id: r.safra_id || null,
      talhao_id: r.talhao_id || null
    }))
  }, [lancamentos])

  const totals = useMemo(() => {
    const rec = normalized.filter(x => x.tipo === 'receita').reduce((acc, x) => acc + x.valor, 0)
    const desp = normalized.filter(x => x.tipo === 'despesa').reduce((acc, x) => acc + x.valor, 0)
    return { rec, desp, saldo: rec - desp }
  }, [normalized])

  const mesesResumo = useMemo(() => {
    const out = []
    for (let m = 0; m < 12; m++) {
      const receitas = normalized.filter(x => {
        const d = new Date(x.data)
        return x.tipo === 'receita' && d.getFullYear() === Number(year) && d.getMonth() === m
      }).reduce((acc, x) => acc + x.valor, 0)
      const despesas = normalized.filter(x => {
        const d = new Date(x.data)
        return x.tipo === 'despesa' && d.getFullYear() === Number(year) && d.getMonth() === m
      }).reduce((acc, x) => acc + x.valor, 0)
      const date = new Date(Number(year), m, 1)
      out.push({
        mesLabel: format(date, 'MMM/yyyy', { locale: ptBR }),
        mesNum: String(m + 1).padStart(2, '0'),
        receitas,
        despesas,
        saldo: receitas - despesas
      })
    }
    return out
  }, [normalized, year])

  const filteredNormalized = useMemo(() => {
    if (monthFilter === 'todos') return normalized
    return normalized.filter(x => {
      const d = new Date(x.data)
      return d.getFullYear() === Number(year) && String(d.getMonth() + 1).padStart(2, '0') === monthFilter
    })
  }, [normalized, monthFilter, year])

  const sanitizeText = (value) => String(value || '').replace(/\|/g, ' ').trim()

  const formatDateLCDPR = (value) => {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return ''
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const yearStr = String(d.getFullYear())
    return `${day}${month}${yearStr}`
  }

  const formatValueCents = (value) => String(Math.round(Number(value || 0) * 100))

  const extractDocDigits = (value) => String(value || '').replace(/\D/g, '')

  const exportCSV = () => {
    const header = ['Data','Tipo','Categoria','Descrição','Pessoa','FormaPagamento','Valor']
    const rows = normalized.map(x => [x.data, x.tipo, x.categoria, x.descricao, x.pessoa, x.forma_pagamento, x.valor.toFixed(2)])
    const csv = [header.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g,'"')}"`).join(','))].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `lcdpr_${year}.csv`
    a.click()
  }

  const exportTXT = () => {
    const contrib = userInfo || {}
    const nome = contrib?.full_name || contrib?.razao_social || user?.email || ''
    const doc = (contrib?.cpf || contrib?.cnpj || '').replace(/\D/g,'')
    const cidade = contrib?.endereco_cidade || ''
    const uf = contrib?.endereco_estado || ''
    const propNome = propriedade?.nome || String(propriedadeId || '')
    const codImovel = '001'
    let saldoCents = 0
    const registro0000 = [
      '0000',
      '1.3',
      formatDateLCDPR(startDate),
      formatDateLCDPR(endDate),
      sanitizeText(nome),
      doc || ''
    ].join('|')
    const registro0040 = [
      '0040',
      codImovel,
      sanitizeText(propNome),
      sanitizeText(cidade),
      sanitizeText(uf),
      '100'
    ].join('|')
    const registrosQ100 = normalized.map((x) => {
      const entradaCents = x.tipo === 'receita' ? Number(formatValueCents(x.valor)) : 0
      const saidaCents = x.tipo === 'despesa' ? Number(formatValueCents(x.valor)) : 0
      saldoCents += entradaCents - saidaCents
      const idPartic = extractDocDigits(x.pessoa) || '00000000000'
      const codConta = String(x.forma_pagamento || '').toLowerCase() === 'dinheiro' ? '000' : '999'
      const tipoLanc = x.tipo === 'receita' ? '1' : '2'
      const saldoAbs = String(Math.abs(saldoCents))
      const natSaldo = saldoCents >= 0 ? 'P' : 'N'
      return [
        'Q100',
        formatDateLCDPR(x.data),
        codImovel,
        codConta,
        '',
        '6',
        sanitizeText(x.descricao || x.categoria),
        idPartic,
        tipoLanc,
        entradaCents ? String(entradaCents) : '0',
        saidaCents ? String(saidaCents) : '0',
        saldoAbs,
        natSaldo
      ].join('|')
    })
    const txt = [registro0000, registro0040, ...registrosQ100].join('\n')
    const blob = new Blob([txt], { type: 'text/plain;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `lcdpr_${year}.txt`
    a.click()
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>LCDPR - Livro Caixa do Produtor Rural</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label>Exercício</Label>
              <Input value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g,''))} />
            </div>
            <div className="space-y-1">
              <Label>Incluir somente pagos/recebidos</Label>
              <Select value={onlyPaid ? 'sim' : 'nao'} onValueChange={(v) => setOnlyPaid(v === 'sim')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sim">Sim</SelectItem><SelectItem value="nao">Não</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Somente dinheiro (caixa)</Label>
              <Select value={onlyCash ? 'sim' : 'nao'} onValueChange={(v) => setOnlyCash(v === 'sim')}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sim">Sim</SelectItem><SelectItem value="nao">Não</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={exportTXT} className="bg-green-700 hover:bg-green-800">Exportar LCDPR</Button>
              <Button onClick={exportCSV} variant="outline">Exportar CSV</Button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary">Receitas: R$ {totals.rec.toFixed(2)}</Badge>
            <Badge variant="secondary">Despesas: R$ {totals.desp.toFixed(2)}</Badge>
            <Badge className={totals.saldo>=0?'bg-green-100 text-green-700':'bg-red-100 text-red-700'}>Saldo: R$ {totals.saldo.toFixed(2)}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label>Filtro detalhado por mês</Label>
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="01">Jan</SelectItem>
                  <SelectItem value="02">Fev</SelectItem>
                  <SelectItem value="03">Mar</SelectItem>
                  <SelectItem value="04">Abr</SelectItem>
                  <SelectItem value="05">Mai</SelectItem>
                  <SelectItem value="06">Jun</SelectItem>
                  <SelectItem value="07">Jul</SelectItem>
                  <SelectItem value="08">Ago</SelectItem>
                  <SelectItem value="09">Set</SelectItem>
                  <SelectItem value="10">Out</SelectItem>
                  <SelectItem value="11">Nov</SelectItem>
                  <SelectItem value="12">Dez</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Card className="border">
            <CardHeader>
              <CardTitle>Resumo Mensal</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-stone-100">
                      <th className="py-2 px-3 text-left">Mês</th>
                      <th className="py-2 px-3 text-right">Receitas</th>
                      <th className="py-2 px-3 text-right">Despesas</th>
                      <th className="py-2 px-3 text-right">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mesesResumo.map(m => (
                      <tr key={m.mesNum} className="border-b">
                        <td className="py-2 px-3">{m.mesLabel}</td>
                        <td className="py-2 px-3 text-right">R$ {m.receitas.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right">R$ {m.despesas.toFixed(2)}</td>
                        <td className={`py-2 px-3 text-right ${m.saldo>=0?'text-green-700':'text-red-700'}`}>R$ {m.saldo.toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="bg-stone-100 font-bold">
                      <td className="py-2 px-3">Total</td>
                      <td className="py-2 px-3 text-right">R$ {mesesResumo.reduce((a,b)=>a+b.receitas,0).toFixed(2)}</td>
                      <td className="py-2 px-3 text-right">R$ {mesesResumo.reduce((a,b)=>a+b.despesas,0).toFixed(2)}</td>
                      <td className={`py-2 px-3 text-right ${mesesResumo.reduce((a,b)=>a+b.saldo,0)>=0?'text-green-700':'text-red-700'}`}>R$ {mesesResumo.reduce((a,b)=>a+b.saldo,0).toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-100">
                  <th className="py-2 px-3 text-left">Data</th>
                  <th className="py-2 px-3 text-left">Tipo</th>
                  <th className="py-2 px-3 text-left">Categoria</th>
                  <th className="py-2 px-3 text-left">Descrição</th>
                  <th className="py-2 px-3 text-left">Pessoa</th>
                  <th className="py-2 px-3 text-left">Forma</th>
                  <th className="py-2 px-3 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={7} className="py-6 text-center">Carregando...</td></tr>
                ) : normalized.length === 0 ? (
                  <tr><td colSpan={7} className="py-6 text-center text-stone-500">Sem lançamentos no período</td></tr>
                ) : (
                  filteredNormalized.map((x, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-2 px-3">{x.data}</td>
                      <td className="py-2 px-3">{x.tipo}</td>
                      <td className="py-2 px-3">{x.categoria}</td>
                      <td className="py-2 px-3">{x.descricao}</td>
                      <td className="py-2 px-3">{x.pessoa}</td>
                      <td className="py-2 px-3">{x.forma_pagamento}</td>
                      <td className="py-2 px-3 text-right">R$ {x.valor.toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
